// Waypoint Selection Dialog - Reusable component for tour creation and editing
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Paper,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

interface Waypoint {
  name?: string;
  destination?: string;
  /** @deprecated use name */
  waypoint_name?: string;
  /** @deprecated use content_name */
  content_name?: string;
  version: number;
  floor: number;
  is_halfway: boolean;
  is_return_point: boolean;
  order?: number;
}

interface WaypointSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (waypoints: Waypoint[]) => void;
  availableWaypoints: Waypoint[];
  loading?: boolean;
}

interface WaypointMeta {
  floor: number;
  is_halfway: boolean;
  is_return_point: boolean;
}

const WaypointSelectionDialog: React.FC<WaypointSelectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  availableWaypoints,
  loading = false,
}) => {
  const [selectedWaypointKeys, setSelectedWaypointKeys] = useState<string[]>([]);
  const [waypointMeta, setWaypointMeta] = useState<Record<string, WaypointMeta>>({});

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedWaypointKeys([]);
      setWaypointMeta({});
    }
  }, [open]);

  const getDefaultMeta = (): WaypointMeta => ({ floor: 1, is_halfway: false, is_return_point: false });

  const updateMeta = (key: string, patch: Partial<WaypointMeta>) => {
    setWaypointMeta(prev => ({
      ...prev,
      [key]: { ...(prev[key] ?? getDefaultMeta()), ...patch },
    }));
  };

  const getWaypointKey = (waypoint: Waypoint, index: number) =>
    `${waypoint.waypoint_name}-${waypoint.version}-${index}`;

  const handleWaypointToggle = (waypoint: Waypoint, index: number) => {
    const key = getWaypointKey(waypoint, index);
    setSelectedWaypointKeys(prevKeys => {
      const isSelected = prevKeys.includes(key);
      if (isSelected) {
        return prevKeys.filter(existingKey => existingKey !== key);
      }

      return [...prevKeys, key];
    });
  };

  const handleConfirm = () => {
    const selectedKeys = new Set(selectedWaypointKeys);
    const selectedWaypoints = availableWaypoints
      .map((waypoint, index) => ({ waypoint, key: getWaypointKey(waypoint, index) }))
      .filter(({ key }) => selectedKeys.has(key))
      .map(({ waypoint, key }) => ({
        ...waypoint,
        ...(waypointMeta[key] ?? getDefaultMeta()),
      }));
    if (selectedWaypoints.length === 0) return;
    onConfirm(selectedWaypoints);
    handleClose();
  };

  const handleClose = () => {
    setSelectedWaypointKeys([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Waypoints and Versions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose one or more waypoints and their content versions to add to your tour.
        </Typography>

        {loading ? (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              Loading waypoints...
            </Typography>
          </Paper>
        ) : availableWaypoints.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              No waypoints available for this museum.
            </Typography>
          </Paper>
        ) : (
          <Box>
            {/* Step 1: Select Waypoints */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Step 1: Select one or more waypoints
              </Typography>
              {availableWaypoints.map((waypoint, index) => {
                const key = getWaypointKey(waypoint, index);
                const isSelected = selectedWaypointKeys.includes(key);
                const label = `${waypoint.name ?? waypoint.content_name ?? ''} → ${waypoint.destination ?? waypoint.waypoint_name ?? ''} (v${waypoint.version})`;
                return (
                  <Box key={key} sx={{ mb: 1 }}>
                    <Button
                      variant={isSelected ? 'contained' : 'outlined'}
                      fullWidth
                      sx={{ justifyContent: 'flex-start' }}
                      onClick={() => handleWaypointToggle(waypoint, index)}
                    >
                      {label}
                    </Button>
                    {isSelected && (
                      <Box
                        sx={{ pl: 2, pt: 1, pb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
                      >
                        <TextField
                          label="Floor"
                          type="number"
                          size="small"
                          value={waypointMeta[key]?.floor ?? 1}
                          onChange={e => updateMeta(key, { floor: parseInt(e.target.value) || 1 })}
                          inputProps={{ min: 1 }}
                          sx={{ width: 90 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={waypointMeta[key]?.is_halfway ?? false}
                              onChange={e => updateMeta(key, { is_halfway: e.target.checked })}
                              size="small"
                            />
                          }
                          label="Halfway point"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={waypointMeta[key]?.is_return_point ?? false}
                              onChange={e => updateMeta(key, { is_return_point: e.target.checked })}
                              size="small"
                            />
                          }
                          label="Return point"
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={selectedWaypointKeys.length === 0}>
          Add Waypoints
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaypointSelectionDialog;
